import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button, Input } from "reactstrap";
import Swal from "sweetalert2";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import { posGet, posPatch, avisaError, dinero, hoy, fechaHora, METODOS } from "./posApi";
import { useTienda, TiendaSelector } from "./TiendaSelector";
import { ticketVenta } from "./posTickets";
import SinPermiso from "./SinPermiso";

function PosVentas() {
  const { user } = useContext(AuthContext);
  const ctx = useTienda(true);
  const { tienda } = ctx;
  const [inicio, setInicio] = useState(hoy());
  const [fin, setFin] = useState(hoy());
  const [ventas, setVentas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const puedeCancelar = !!(user && user.posCancelar);

  const cargar = useCallback(() => {
    if (!tienda) return;
    setCargando(true);
    posGet("/ventas", { area: tienda, inicio, fin })
      .then(setVentas).catch(avisaError).finally(() => setCargando(false));
  }, [tienda, inicio, fin]);
  useEffect(() => { cargar(); }, [cargar]);

  const imprimir = async (v) => {
    try { ticketVenta(await posGet(`/ventas/${v._id}`, { area: v.areas._id })); } catch (e) { avisaError(e); }
  };

  const cancelar = async (v) => {
    const r = await Swal.fire({
      title: `¿Cancelar la venta ${v.folio}?`, text: "La mercancía regresa al inventario de la tienda.",
      input: "text", inputPlaceholder: "Motivo", icon: "warning",
      showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No",
      inputValidator: (x) => (!x ? "Escribe el motivo" : undefined),
    });
    if (!r.value) return;
    try {
      await posPatch(`/ventas/${v._id}/cancelar`, { area: v.areas._id, motivo: r.value });
      Swal.fire({ icon: "success", title: "Venta cancelada", timer: 1500, showConfirmButton: false });
      cargar();
    } catch (e) { avisaError(e); }
  };

  const activas = ventas.filter((v) => v.estatus === "Activa");
  const suma = (m) => activas.reduce((t, v) => t + v.pagos.filter((p) => p.metodo === m).reduce((s, p) => s + p.importe, 0), 0);
  const total = activas.reduce((t, v) => t + v.total, 0);

  if (!(user && user.rolTiendas && user.posVentas)) return <SinPermiso que="consultar ventas" />;

  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-file-invoice-dollar mr-2" style={{ color: "#6366f1" }} />Ventas</h3>
            <p className="page-subtitle">Reimprime tickets o cancela ventas que aún no entran a corte</p>
          </div>
          <div className="page-actions">
            <Button color="secondary" size="sm" href="/PuntoVenta"><i className="fas fa-th-large mr-1" />Menú</Button>
          </div>
        </div>

        <div className="filter-bar">
          <TiendaSelector ctx={ctx} permiteTodas />
          <div><label>Desde</label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></div>
          <div><label>Hasta</label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></div>
          <div><Button color="primary" onClick={cargar} disabled={cargando}>{cargando ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-sync mr-1" />Actualizar</>}</Button></div>
        </div>

        <div className="stats-row">
          <div className="stat-chip"><i className="fas fa-receipt" style={{ color: "#10b981" }} />Tickets <span>{activas.length}</span></div>
          <div className="stat-chip"><i className="fas fa-dollar-sign" style={{ color: "#0f172a" }} />Total <span>{dinero(total)}</span></div>
          {["efectivo", "tdc", "transfer", "cambio"].map((m) => (
            <div key={m} className="stat-chip">{METODOS[m]} <span>{dinero(suma(m))}</span></div>
          ))}
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-dark-header">
            <thead>
              <tr>
                <th>Folio</th><th>Fecha</th>{tienda === "todas" && <th>Tienda</th>}<th>Atendió</th>
                <th className="text-right">Piezas</th><th className="text-right">Descuento</th><th className="text-right">Total</th>
                <th>Pago</th><th>Estatus</th><th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {!ventas.length && <tr><td colSpan={10} className="text-center text-muted py-4">{cargando ? "Cargando…" : "Sin ventas en el periodo"}</td></tr>}
              {ventas.map((v) => (
                <tr key={v._id} style={v.estatus === "Cancelada" ? { color: "#94a3b8" } : {}}>
                  <td>{v.folio}</td>
                  <td>{fechaHora(v.fecha)}</td>
                  {tienda === "todas" && <td>{v.areas && v.areas.name}</td>}
                  <td>{v.user ? v.user.nombre : ""}</td>
                  <td className="text-right">{v.piezas}</td>
                  <td className="text-right">{v.descuento > 0 ? dinero(v.descuento) : ""}</td>
                  <td className="text-right"><strong>{dinero(v.total)}</strong></td>
                  <td>{v.pagos.map((p) => METODOS[p.metodo]).join(" + ")}</td>
                  <td>
                    {v.devueltoImporte > 0 && <span className="badge badge-warning mr-1" title="Saldo a favor reconocido por devoluciones">Dev. {dinero(v.devueltoImporte)}</span>}
                    {v.estatus === "Cancelada"
                      ? <span className="badge badge-danger" title={v.motivoCancelacion}>Cancelada</span>
                      : v.cortes ? <span className="badge badge-secondary">En corte</span> : <span className="badge badge-success">Activa</span>}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Button size="sm" color="info" onClick={() => imprimir(v)} title="Ticket"><i className="fas fa-print" /></Button>{" "}
                    {puedeCancelar && v.estatus === "Activa" && !v.cortes && !v.devolucion && !(v.devueltoImporte > 0) && (
                      <Button size="sm" color="danger" onClick={() => cancelar(v)} title="Cancelar"><i className="fas fa-ban" /></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default PosVentas;
